import { Input, Label, Textarea } from "@repo/ui";
import { Check } from "lucide-react";
import { useId, type ReactNode } from "react";
import {
  questionsVisibles,
  type EcranQuestions,
  type QuestionEcran,
  valeurReponse,
  type SaisieEcrans,
  type ValeurSaisie,
} from "./copsoq3-ecrans";
import { OptionCopsoq3, PastilleOption } from "./copsoq3-option";

interface EcranProps {
  ecran: EcranQuestions;
  ecrans: readonly EcranQuestions[];
  saisie: SaisieEcrans;
  editable: boolean;
  onChange: (name: string, valeur: ValeurSaisie) => void;
}

/** Un écran thématique des parties 1 ou 3 : ses questions visibles, celles d'une
 * même branche regroupées dans une section titrée. */
export function Copsoq3Ecran({
  ecran,
  ecrans,
  saisie,
  editable,
  onChange,
}: EcranProps) {
  const groupes = grouperParRubrique(questionsVisibles(ecran, saisie, ecrans));
  const champ = (q: QuestionEcran) => (
    <QuestionChamp
      key={q.name}
      question={q}
      valeur={saisie[q.name]}
      editable={editable}
      onChange={(v) => onChange(q.name, v)}
    />
  );

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-medium">{ecran.titre}</h2>
      {groupes.map(({ rubrique, questions }) =>
        rubrique === undefined ? (
          questions.map(champ)
        ) : (
          <SectionRubrique key={rubrique} rubrique={rubrique}>
            {questions.map(champ)}
          </SectionRubrique>
        ),
      )}
    </div>
  );
}

function grouperParRubrique(questions: QuestionEcran[]) {
  const groupes: { rubrique?: string; questions: QuestionEcran[] }[] = [];
  for (const q of questions) {
    const dernier = groupes.at(-1);
    if (dernier && dernier.rubrique === q.rubrique) dernier.questions.push(q);
    else groupes.push({ rubrique: q.rubrique, questions: [q] });
  }
  return groupes;
}

function SectionRubrique({
  rubrique,
  children,
}: {
  rubrique: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      className="space-y-6 border-t border-border pt-4"
    >
      <h3
        id={id}
        className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {rubrique}
      </h3>
      {children}
    </section>
  );
}

function QuestionChamp({
  question,
  valeur,
  editable,
  onChange,
}: {
  question: QuestionEcran;
  valeur: ValeurSaisie | undefined;
  editable: boolean;
  onChange: (valeur: ValeurSaisie) => void;
}) {
  const id = useId();

  if (question.type === "choix" || question.type === "choix-multiple") {
    const multiple = question.type === "choix-multiple";
    const cochees = Array.isArray(valeur) ? valeur : [];
    return (
      <div role="group" aria-labelledby={id} className="space-y-3">
        <p id={id} className="font-medium leading-relaxed">
          {question.question}
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {question.options.map((option) => {
            const selectionne = multiple
              ? cochees.includes(option)
              : valeur === option;
            return (
              <OptionCopsoq3
                key={option}
                compact
                label={option}
                selectionne={selectionne}
                disabled={!editable}
                onClick={() =>
                  onChange(
                    !multiple
                      ? option
                      : selectionne
                        ? cochees.filter((o) => o !== option)
                        : [...cochees, option],
                  )
                }
                indicateur={
                  <PastilleOption selectionne={selectionne} carre={multiple}>
                    {selectionne &&
                      (multiple ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-current" />
                      ))}
                  </PastilleOption>
                }
              />
            );
          })}
        </div>
      </div>
    );
  }

  const texte = typeof valeur === "string" ? valeur : "";
  const invalide =
    texte.trim() !== "" && valeurReponse(question, texte) === undefined;
  const aide =
    question.type === "nombre"
      ? `Nombre entier entre ${question.min} et ${question.max}`
      : question.type === "departement"
        ? "Numéro de département (ex. 57, 2A, 974)"
        : undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="font-medium leading-relaxed">
        {question.question}
        {question.facultative && (
          <span className="font-normal text-muted-foreground">
            {" "}
            (facultatif)
          </span>
        )}
      </Label>
      {question.type === "texte" && question.multiligne ? (
        <Textarea
          id={id}
          value={texte}
          disabled={!editable}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          id={id}
          value={texte}
          disabled={!editable}
          aria-invalid={invalide}
          aria-describedby={aide && `${id}-aide`}
          className={question.type === "texte" ? undefined : "max-w-40"}
          {...(question.type === "nombre" && {
            type: "number",
            inputMode: "numeric",
            min: question.min,
            max: question.max,
          })}
          {...(question.type === "departement" && { maxLength: 3 })}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {aide && (
        <p
          id={`${id}-aide`}
          className={
            invalide
              ? "text-xs text-destructive"
              : "text-xs text-muted-foreground"
          }
        >
          {aide}
        </p>
      )}
    </div>
  );
}
